'use client';

import { Card } from '@app/app/_components/Card/Card';
import { CollapsablePanel } from '@app/app/_components/CollapsablePanel/CollapsablePanel';
import { RadioButtonInput } from '@app/app/_components/Input/RadioButtonInput';
import { Select } from '@app/app/_components/Input/Select/Select';
import { ReadOnlyField } from '@app/app/_components/ReadOnlyField/ReadOnlyField';
import { DiscountCodeData } from '@app/domain/model/eshop/transport/DiscountCodeData';
import { ProductDataPurpose } from '@app/domain/model/eshop/transport/ProductDataPurpose';
import { VehicleInformation } from '@app/domain/model/eshop/transport/VehicleInformation';
import { FormState } from '@app/lib/form/contracts/FormState';
import { convertCCtoLitre } from '@app/lib/unit-converter/convertCCtoLitre';
import { PurchaseTransportInsuranceResult } from '@app/service/eshop/transport/actions/purchaseTransportInsurance';
import { useTranslations } from 'next-intl';
import { ChangeEvent, Dispatch, SetStateAction, useCallback, useMemo } from 'react';
import { DiscountCode } from '../DiscountCode';
import { InsuranceDuration } from '@app/domain/model/eshop/transport/InsuranceDuration';
import { InformationPopover } from '@app/app/_components/InformationPopover/InformationPopover';
import { QuestionCircleIcon } from '@app/app/_icons/QuestionCircleIcon';
import { Heading } from '@app/app/_components/Heading/Heading';

interface Props {
    vehicleInformation: VehicleInformation;
    formState: FormState<PurchaseTransportInsuranceResult>;
    handleDiscountCode: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleApplyDiscountClick: () => void;
    discountCodeData: DiscountCodeData;
    mtplPurposes: Nullable<ProductDataPurpose>;
    setInsuranceDuration: Dispatch<SetStateAction<string>>;
}

const getInitialMtplPurpose = (
    formState: FormState<PurchaseTransportInsuranceResult>,
    mtplPurposes: Nullable<ProductDataPurpose>,
    defaultPurpose: Nullable<{ name: string; code: string }>,
): Nullable<{ name: string; code: string }> => {
    const savedPurpose = formState.data?.formData.purposeOfTheVehicle;
    if (savedPurpose && mtplPurposes) {
        const foundPurpose = mtplPurposes.purposes.find(purpose => purpose.code === savedPurpose);
        if (foundPurpose) return foundPurpose;
    }
    return defaultPurpose ?? null;
};

export function InsuranceTermsStep({
    vehicleInformation,
    formState,
    handleDiscountCode,
    handleApplyDiscountClick,
    discountCodeData,
    mtplPurposes,
    setInsuranceDuration,
}: Props) {
    const t = useTranslations();

    const purposeOfTheVehicleError = formState.violations?.purposeOfTheVehicle;

    const defaultPurpose = useMemo(() => {
        return mtplPurposes?.purposes.find(purpose => purpose.code === mtplPurposes.defaultPurpose);
    }, [mtplPurposes]);

    const initialMtplPurpose = useMemo(
        () => getInitialMtplPurpose(formState, mtplPurposes, defaultPurpose ?? null),
        [formState, mtplPurposes, defaultPurpose],
    );

    const mtplPurposeOptions = useMemo(() => {
        if (!mtplPurposes || !mtplPurposes.purposes || mtplPurposes.purposes.length === 0) {
            return [];
        }
        return mtplPurposes.purposes.map(purpose => ({
            label: purpose.name,
            value: purpose.code,
        }));
    }, [mtplPurposes]);

    const onChangeInsuranceDuration = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            setInsuranceDuration(e.target.value);
        },
        [setInsuranceDuration],
    );

    return (
        <div className="flex flex-col gap-8">
            <Card className="gap-6 p-6">
                <CollapsablePanel
                    collapsable
                    defaultCollapsed
                    label={`${vehicleInformation.make} ${formState.data?.formData.modelCASCO ?? vehicleInformation.modelCASCO ?? ''} / ${vehicleInformation.licencePlate} / ${vehicleInformation.manufactureYear}`}>
                    <div className="grid grid-cols-1 gap-4 px-6 pt-2 xs:grid-cols-2">
                        <ReadOnlyField label={t('eshop.transport.vehicleModelLabel')}>
                            {formState.data?.formData.modelCASCO ?? vehicleInformation.modelCASCO}
                        </ReadOnlyField>
                        <ReadOnlyField label={t('eshop.transport.vehicleYearLabel')}>
                            {vehicleInformation.manufactureYear}
                        </ReadOnlyField>
                        <ReadOnlyField label={t('eshop.transport.vehicleWeightLabel')}>
                            {t('general.units.kg', { value: vehicleInformation.weight })}
                        </ReadOnlyField>
                        <ReadOnlyField label={t('eshop.transport.vehiclePowerLabel')}>
                            {t('general.units.kw', { value: vehicleInformation.enginePower })}
                        </ReadOnlyField>
                        <ReadOnlyField label={t('eshop.transport.vehicleEngineLabel')}>
                            {t('general.units.litre', {
                                value: convertCCtoLitre(vehicleInformation.engineCapacity ?? 0).toFixed(1),
                            })}
                        </ReadOnlyField>
                    </div>
                </CollapsablePanel>
                <div className="flex flex-col gap-4 px-6">
                    <div className="flex flex-row items-center gap-2">
                        <Heading level="h3">{t('eshop.transport.insurancePeriodLabel')}</Heading>
                        <InformationPopover content={t('eshop.transport.insurancePeriodTooltip')}>
                            <QuestionCircleIcon className="size-5 text-neutral-500" />
                        </InformationPopover>
                    </div>
                    <RadioButtonInput
                        name="insuranceDuration"
                        error={formState.violations?.insuranceDuration}
                        defaultValue={formState.data?.formData.insuranceDuration}
                        onChange={onChangeInsuranceDuration}
                        choices={[
                            {
                                value: InsuranceDuration.TwelveMonths,
                                label: '12',
                            },
                            {
                                value: InsuranceDuration.SixMonths,
                                label: '6',
                            },
                            {
                                value: InsuranceDuration.ThreeMonths,
                                label: '3',
                            },
                            {
                                value: InsuranceDuration.TwoMonths,
                                label: '2',
                            },
                            {
                                value: InsuranceDuration.OneMonth,
                                label: '1',
                            },
                        ]}
                    />
                </div>
                <div className="flex flex-col gap-4 px-6">
                    <div className="flex flex-row items-center gap-2">
                        <Heading level="h3">{t('eshop.transport.purposeOfTheVehicle')}</Heading>
                        <InformationPopover content={t('eshop.transport.vehiclePurposeTooltip')}>
                            <QuestionCircleIcon className="size-5 text-neutral-500" />
                        </InformationPopover>
                    </div>
                    {mtplPurposeOptions.length > 0 && (
                        <Select
                            options={mtplPurposeOptions}
                            name="purposeOfTheVehicle"
                            defaultValue={initialMtplPurpose?.code}
                            error={purposeOfTheVehicleError}
                        />
                    )}
                </div>
            </Card>

            <DiscountCode
                handleDiscountCode={handleDiscountCode}
                handleApplyDiscountClick={handleApplyDiscountClick}
                discountCodeData={discountCodeData}
            />
        </div>
    );
}
